import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, ArrowRight, Banknote, BedDouble, CalendarClock,
  CheckCircle2, Clock, Hospital, Inbox, Loader2, RefreshCw, ShieldAlert,
  ShieldCheck, Stethoscope, TrendingDown, TrendingUp,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { plannerNavItems } from '../components/brand/navItems';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOW_OPTIONS = [
  { v: 30, l: 'Last 30 days' },
  { v: 90, l: 'Last 90 days' },
  { v: 180, l: 'Last 6 months' },
  { v: 365, l: 'Last 12 months' },
];

const fmtMin = (m) => (m == null ? '—' : (m < 60 ? `${m.toFixed(0)}m` : `${(m / 60).toFixed(1)}h`));
const fmtUsd = (n) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const fmtPct = (n) => (n == null ? '—' : `${n.toFixed(1)}%`);

const KpiTile = ({ icon: Icon, label, value, sub, tone = 'bg-cn-soft text-cn-primary', testId }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={testId}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-display font-semibold text-cn-text leading-tight">{value}</div>
    <div className="text-sm text-cn-text-muted mt-0.5">{label}</div>
    {sub && <div className="text-[11px] text-cn-text-muted/80 mt-1">{sub}</div>}
  </div>
);

const LatencyTile = ({ label, lat, testId }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={testId}>
    <div className="text-sm font-bold uppercase tracking-wide text-cn-text-muted mb-3">{label}</div>
    {lat?.n ? (
      <>
        <div className="grid grid-cols-3 gap-2">
          {[['p50', lat.p50_min], ['p75', lat.p75_min], ['p95', lat.p95_min]].map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase font-bold text-cn-text-muted">{k}</div>
              <div className="text-2xl font-display font-semibold text-cn-text">{fmtMin(v)}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-cn-text-muted/80 mt-3">n={lat.n} observations</div>
      </>
    ) : (
      <div className="text-sm text-cn-text-muted py-3">No data in window.</div>
    )}
  </div>
);

const CohortVsBaselineBar = ({ cohort, baseline }) => {
  if (cohort == null && baseline == null) return null;
  const max = Math.max(20, Math.ceil(Math.max(cohort || 0, baseline || 0) + 5));
  const winner = cohort != null && cohort < (baseline || 0);
  return (
    <div className="cn-card rounded-2xl p-5" data-testid="spine-scoreboard-cohort-vs-baseline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="cn-display text-lg text-cn-text">CaraLoom cohort vs. baseline</h3>
        <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${winner ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
          {winner ? <><TrendingDown className="inline w-3 h-3 mr-0.5" /> outperforming</> : 'monitoring'}
        </span>
      </div>
      <div className="space-y-4">
        {[
          { label: 'Baseline (CMS-aligned)', value: baseline, tone: 'bg-slate-300' },
          { label: 'CaraLoom cohort', value: cohort, tone: winner ? 'bg-emerald-500' : 'bg-orange-500' },
        ].map((row) => (
          <div key={row.label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-cn-text-muted">{row.label}</span>
              <span className="text-cn-text font-semibold">{fmtPct(row.value)}</span>
            </div>
            <div className="h-3 rounded-full bg-cn-soft overflow-hidden">
              <div
                className={`h-full rounded-full ${row.tone}`}
                style={{ width: `${row.value ? Math.min(100, (row.value / max) * 100) : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const WeeklyTrend = ({ series }) => {
  if (!series?.length) return null;
  const maxTotal = Math.max(1, ...series.map((s) => s.total));
  return (
    <div className="cn-card rounded-2xl p-5" data-testid="spine-scoreboard-weekly-trend">
      <h3 className="cn-display text-lg text-cn-text mb-4">Acceptance rate trend</h3>
      <div className="grid grid-cols-12 gap-1 items-end h-32">
        {series.slice(-12).map((s) => (
          <div key={s.week_start} className="flex flex-col items-center group" title={`Week of ${s.week_start} · ${s.placed}/${s.placed + s.bounced} placed · ${s.acceptance_rate_pct ?? '—'}%`}>
            <div className="w-full flex flex-col-reverse h-full" data-testid={`spine-scoreboard-trend-week-${s.week_start}`}>
              <div className="bg-cn-soft" style={{ height: `${(s.bounced / maxTotal) * 100}%` }} />
              <div className="bg-emerald-500 group-hover:bg-emerald-600 transition-colors" style={{ height: `${(s.placed / maxTotal) * 100}%` }} />
            </div>
            <div className="text-[9px] text-cn-text-muted mt-1 truncate w-full text-center">
              {s.week_start.slice(5)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-3 text-[11px] text-cn-text-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Placed</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-cn-soft border border-cn-border" /> Bounced</span>
      </div>
    </div>
  );
};

const TierOutcomesPanel = ({ data }) => {
  const tiers = Object.entries(data || {});
  return (
    <div className="cn-card rounded-2xl p-5" data-testid="spine-scoreboard-tier-outcomes">
      <h3 className="cn-display text-lg text-cn-text mb-4">Outcomes by risk tier</h3>
      {!tiers.length && (
        <p className="text-sm text-cn-text-muted">No closed watches in window.</p>
      )}
      <div className="space-y-3">
        {tiers.map(([tier, v]) => {
          const readmitPct = v.total ? (100 * v.readmitted) / v.total : 0;
          const avoidedPct = v.total ? (100 * v.avoided) / v.total : 0;
          return (
            <div key={tier} data-testid={`spine-scoreboard-tier-${tier}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold text-cn-text capitalize">{tier.replace('_', ' ')}</span>
                <span className="text-cn-text-muted text-xs">n={v.total}</span>
              </div>
              <div className="h-3 rounded-full bg-cn-soft overflow-hidden flex">
                <div className="bg-red-400" style={{ width: `${readmitPct}%` }} title={`Readmitted ${v.readmitted}`} />
                <div className="bg-emerald-500" style={{ width: `${avoidedPct}%` }} title={`Avoided ${v.avoided}`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function SpineHospitalScoreboard() {
  const { orgId: paramOrgId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [orgId, setOrgId] = useState(paramOrgId || '');
  const [orgs, setOrgs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(90);

  // Planner-side users get the spine sidebar; admin keeps the default
  // (empty) nav so the global admin layout doesn't change.
  const showPlannerNav = user?.role && user.role !== 'admin';
  const planNav = plannerNavItems({ pathname: '/spine/scoreboard' });

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/organizations`, getAuthHeader());
        if (!ok) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setOrgs(list);
        if (!orgId && list.length) setOrgId(list[0].id);
      } catch (e) {
        toast.error('Failed to load organizations');
      }
    })();
    return () => { ok = false; };
  }, [getAuthHeader]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/spine/analytics/${orgId}?window_days=${window}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error(`Entitlement disabled: ${detail?.entitlement_required || 'hospital_scoreboard'}`);
      } else if (e.response?.status === 403) {
        toast.error('Not authorized for this org');
      } else {
        toast.error('Failed to load scoreboard');
      }
      setData(null);
    } finally { setLoading(false); }
  }, [getAuthHeader, orgId, window]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (paramOrgId && paramOrgId !== orgId) setOrgId(paramOrgId);
  }, [paramOrgId]);

  const kpis = data?.kpis;
  const trendOk = useMemo(() => Array.isArray(data?.weekly_series) && data.weekly_series.length > 0, [data]);

  const handleOrgChange = (next) => {
    setOrgId(next);
    if (user?.role === 'admin') navigate(`/spine/admin/scoreboard/${next}`);
  };

  return (
    <DashboardShell
      title="Hospital scoreboard"
      subtitle="The six numbers that matter — referral latency, time-to-first-visit, readmission, acceptance, avoidable bed-days, dollars protected."
      navItems={showPlannerNav ? planNav : []}
      testId="spine-hospital-scoreboard"
      rightSlot={
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={orgId} onValueChange={handleOrgChange}>
            <SelectTrigger className="h-10 rounded-xl min-w-[200px]" data-testid="spine-scoreboard-org-select">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent className="max-h-[60vh]">
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(window)} onValueChange={(v) => setWindow(parseInt(v, 10))}>
            <SelectTrigger className="h-10 rounded-xl min-w-[150px]" data-testid="spine-scoreboard-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTIONS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={load} className="cn-btn !bg-white !text-cn-primary !border !border-cn-border" data-testid="spine-scoreboard-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : !data ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-scoreboard-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Hospital className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">Pick an organization</h3>
          <p className="text-cn-text-muted max-w-md mx-auto">
            Scoreboard surfaces appear once data lands. All analytics are computed from PHI-free events.
          </p>
        </div>
      ) : (
        <>
          {/* Org header */}
          <div className="cn-card rounded-2xl p-5 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="spine-scoreboard-header">
            <div>
              <div className="text-sm text-cn-text-muted">Showing</div>
              <div className="cn-display text-2xl text-cn-text">{data.org.name}</div>
              <div className="text-xs text-cn-text-muted mt-0.5 capitalize">
                {data.org.type?.replace(/_/g, ' ')} · {data.org.primary_state || '—'} · window {data.window.days}d
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-bold text-cn-text-muted">Baseline 30-day readmit</div>
              <div className="text-3xl font-display font-semibold text-cn-text">{fmtPct(data.org.baseline_30d_readmit_rate)}</div>
            </div>
          </div>

          {/* The six KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiTile
              icon={Clock} label="Acceptance latency · p50"
              value={fmtMin(kpis.referral_latency.p50_min)}
              sub={`p95 ${fmtMin(kpis.referral_latency.p95_min)} · n=${kpis.referral_latency.n}`}
              tone="bg-blue-100 text-blue-700"
              testId="spine-scoreboard-kpi-acceptance-latency"
            />
            <KpiTile
              icon={CalendarClock} label="Time-to-first-visit · p50"
              value={fmtMin(kpis.first_visit_latency.p50_min)}
              sub={`p95 ${fmtMin(kpis.first_visit_latency.p95_min)} · n=${kpis.first_visit_latency.n}`}
              tone="bg-emerald-100 text-emerald-700"
              testId="spine-scoreboard-kpi-first-visit-latency"
            />
            <KpiTile
              icon={ShieldAlert} label="30-day readmit · cohort"
              value={fmtPct(kpis.cohort_readmit_rate_pct)}
              sub={`vs baseline ${fmtPct(kpis.baseline_readmit_rate_pct)} · Δ ${kpis.delta_pct_points} pp`}
              tone={kpis.delta_pct_points > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}
              testId="spine-scoreboard-kpi-readmit-rate"
            />
            <KpiTile
              icon={CheckCircle2} label="Acceptance rate"
              value={fmtPct(kpis.acceptance_rate_pct)}
              sub={`${kpis.placed} placed · ${kpis.bounced} bounced · ${kpis.pending} in-flight`}
              tone="bg-cn-soft text-cn-primary"
              testId="spine-scoreboard-kpi-acceptance-rate"
            />
            <KpiTile
              icon={BedDouble} label="Avoidable bed-days"
              value={kpis.avoidable_bed_days}
              sub={`assumes avg LOS ${kpis.assumptions.avg_los_days}d`}
              tone="bg-violet-100 text-violet-700"
              testId="spine-scoreboard-kpi-avoidable-bed-days"
            />
            <KpiTile
              icon={Banknote} label="Penalty $ protected"
              value={fmtUsd(kpis.penalty_dollars_protected)}
              sub={`assumes ${fmtUsd(kpis.assumptions.penalty_per_excess_usd)} / excess readmit`}
              tone="bg-emerald-100 text-emerald-700"
              testId="spine-scoreboard-kpi-dollars-protected"
            />
          </div>

          {/* Latency tiles + cohort comparison */}
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <LatencyTile label="Acceptance latency distribution" lat={kpis.referral_latency} testId="spine-scoreboard-latency-accept" />
            <LatencyTile label="First-visit latency distribution" lat={kpis.first_visit_latency} testId="spine-scoreboard-latency-fv" />
            <CohortVsBaselineBar cohort={kpis.cohort_readmit_rate_pct} baseline={kpis.baseline_readmit_rate_pct} />
          </div>

          {/* Outcomes + trend */}
          <div className="grid lg:grid-cols-2 gap-4">
            <TierOutcomesPanel data={data.tier_outcomes} />
            {trendOk && <WeeklyTrend series={data.weekly_series} />}
          </div>

          {/* Footer note */}
          <div className="mt-6 text-[11px] text-cn-text-muted/80 inline-flex items-center gap-1" data-testid="spine-scoreboard-phi-note">
            <ShieldCheck className="w-3.5 h-3.5" />
            Computed from PHI-free events · {kpis.referrals_total} referrals · {data.kpis.active_watches} active watches · generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </>
      )}
    </DashboardShell>
  );
}
